/**
 * CrmProductCategories - Página de gerenciamento de categorias de produtos
 * 
 * Características:
 * - Visualização hierárquica em árvore
 * - CRUD completo via Dialog
 * - Seleção de ícone e cor
 * - Toggle de status ativo/inativo
 * - Suporte a múltiplos workspaces por categoria
 * 
 * @author Sistema
 * @version 2.0.0
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  Pencil,
  Trash2,
  FolderPlus,
  Folder,
  FolderTree,
  Box,
  Package,
  Wrench,
  Laptop,
  Smartphone,
  Monitor,
  Headphones,
  Car,
  Home,
  Building,
  Coffee,
  ShoppingCart,
  Gift,
  Heart,
  Star,
  Zap,
  Target,
  Layers,
  RefreshCw,
  Loader2,
  CheckCircle,
  Search,
  GitBranch,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { 
  useCrmCategories, 
  CrmProductCategory, 
  CreateCategoryInput,
  UpdateCategoryInput 
} from '@/hooks/useCrmCategories';
import { useCrmItemWorkspaces } from '@/hooks/useCrmItemWorkspaces';
import { CategoryTreeSelect } from '@/components/crm/CategoryTreeSelect';
import { WorkspaceMultiSelect } from '@/components/crm/WorkspaceMultiSelect';
import { usePermissions } from '@/hooks/usePermissions';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/** Interface para estatísticas de categorias (KPIs) */
interface CategoryStats {
  total_count: number;       // Total de categorias
  active_count: number;      // Categorias ativas
  parent_count: number;      // Categorias pai (raiz)
  child_count: number;       // Subcategorias
}

// ============================================================================
// COMPONENTES AUXILIARES PREMIUM
// ============================================================================

/** Props do MetricCard */
interface MetricCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  variant?: 'primary' | 'success' | 'info' | 'purple';
}

/**
 * Estilos de variantes para KPI cards - padrão horizontal compacto
 */
const METRIC_CARD_STYLES: Record<string, { cardBg: string; iconColor: string }> = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/** Card de métrica compacto horizontal - padrão unificado CRM */
function MetricCard({ title, value, icon: Icon, variant = 'primary' }: MetricCardProps) {
  const styles = METRIC_CARD_STYLES[variant] || METRIC_CARD_STYLES.primary;

  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/** Props do EmptyState */
interface EmptyStateProps {
  title: string;
  description: string;
  icon: LucideIcon;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/** Componente de estado vazio premium */
function EmptyState({ title, description, icon: Icon, action }: EmptyStateProps) {
  return (
    <div className="relative rounded-xl border-2 border-dashed border-border/50 bg-gradient-to-br from-muted/20 to-muted/5 p-8 text-center">
      <div className="mx-auto w-fit p-4 rounded-full bg-muted/50 mb-4">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="font-semibold text-foreground mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">{description}</p>
      {action && (
        <Button variant="outline" size="sm" onClick={action.onClick}>
          <Plus className="h-4 w-4 mr-1.5" />
          {action.label}
        </Button>
      )}
    </div>
  );
}

// Lista de ícones disponíveis
const AVAILABLE_ICONS = [
  { name: 'folder', icon: Folder },
  { name: 'folder-tree', icon: FolderTree },
  { name: 'box', icon: Box },
  { name: 'package', icon: Package },
  { name: 'wrench', icon: Wrench },
  { name: 'laptop', icon: Laptop },
  { name: 'smartphone', icon: Smartphone },
  { name: 'monitor', icon: Monitor },
  { name: 'headphones', icon: Headphones },
  { name: 'car', icon: Car },
  { name: 'home', icon: Home },
  { name: 'building', icon: Building },
  { name: 'coffee', icon: Coffee },
  { name: 'shopping-cart', icon: ShoppingCart },
  { name: 'gift', icon: Gift },
  { name: 'heart', icon: Heart },
  { name: 'star', icon: Star },
  { name: 'zap', icon: Zap },
  { name: 'target', icon: Target },
];

// Cores predefinidas
const PRESET_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#a855f7', // Purple
  '#d946ef', // Fuchsia
  '#ec4899', // Pink
  '#f43f5e', // Rose
  '#ef4444', // Red
  '#f97316', // Orange
  '#f59e0b', // Amber
  '#eab308', // Yellow
  '#84cc16', // Lime
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#0ea5e9', // Sky
  '#3b82f6', // Blue
];

/**
 * Obtém o componente de ícone pelo nome
 */
function getIconComponent(iconName: string) {
  const found = AVAILABLE_ICONS.find(i => i.name === iconName);
  return found?.icon || Folder;
}

/**
 * Componente de item de categoria na árvore
 */
interface CategoryNodeProps {
  category: CrmProductCategory;
  level: number;
  onEdit?: (category: CrmProductCategory) => void;
  onAddChild?: (parentId: string) => void;
  onDelete?: (category: CrmProductCategory) => void;
  onToggleActive?: (category: CrmProductCategory) => void;
}

function CategoryNode({ 
  category, 
  level, 
  onEdit, 
  onAddChild, 
  onDelete,
  onToggleActive 
}: CategoryNodeProps) {
  const [expanded, setExpanded] = React.useState(true);
  const hasChildren = category.children && category.children.length > 0;
  const IconComponent = getIconComponent(category.icon);

  return (
    <div className="select-none">
      {/* Item da categoria */}
      <div
        className={cn(
          'flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-muted/50 group',
          !category.is_active && 'opacity-50'
        )}
        style={{ marginLeft: `${level * 20}px` }}
      >
        {/* Botão de expandir/colapsar */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            'h-5 w-5 flex items-center justify-center rounded hover:bg-muted',
            !hasChildren && 'invisible'
          )}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        {/* Ícone com cor */}
        <div
          className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${category.color}20` }}
        >
          <IconComponent 
            className="h-4 w-4" 
            style={{ color: category.color }} 
          />
        </div>

        {/* Nome e descrição */}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{category.name}</div>
          {category.description && (
            <div className="text-xs text-muted-foreground truncate">
              {category.description}
            </div>
          )}
        </div>

        {/* Badge de status */}
        {!category.is_active && (
          <Badge variant="outline" className="text-xs">
            Inativa
          </Badge>
        )}

        {/* Menu de ações - condicionado por permissões */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 opacity-0 group-hover:opacity-100"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEdit && (
              <DropdownMenuItem onClick={() => onEdit(category)}>
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
            )}
            {onAddChild && (
              <DropdownMenuItem onClick={() => onAddChild(category.id)}>
                <FolderPlus className="mr-2 h-4 w-4" />
                Nova subcategoria
              </DropdownMenuItem>
            )}
            {onToggleActive && (
              <DropdownMenuItem onClick={() => onToggleActive(category)}>
                <Switch
                  checked={category.is_active}
                  className="mr-2 h-4 w-7 pointer-events-none"
                />
                {category.is_active ? 'Desativar' : 'Ativar'}
              </DropdownMenuItem>
            )}
            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(category)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Subcategorias */}
      {hasChildren && expanded && (
        <div>
          {category.children!.map(child => (
            <CategoryNode
              key={child.id}
              category={child}
              level={level + 1}
              onEdit={onEdit}
              onAddChild={onAddChild}
              onDelete={onDelete}
              onToggleActive={onToggleActive}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Página principal de categorias
 */
export default function CrmProductCategories() {
  const { t } = useTranslation('crm');

  // === Controle de permissões granular ===
  const { canCreate: canCreatePerm, canEdit: canEditPerm, canDelete: canDeletePerm } = usePermissions();
  const MENU_KEY = 'crm_categories';
  const hasCreatePermission = canCreatePerm(MENU_KEY);
  const hasEditPermission = canEditPerm(MENU_KEY);
  const hasDeletePermission = canDeletePerm(MENU_KEY);

  const {
    categoryTree,
    categories,
    isLoading,
    createCategory,
    updateCategory,
    deleteCategory,
    refetch,
  } = useCrmCategories();
  const { updateItemWorkspaces, fetchItemWorkspaces } = useCrmItemWorkspaces();

  // Estado de busca
  const [search, setSearch] = React.useState('');

  // ============================================================================
  // ESTATÍSTICAS CALCULADAS
  // ============================================================================

  /** Achata a árvore para contagem */
  const flattenTree = React.useCallback((tree: CrmProductCategory[]): CrmProductCategory[] => {
    return tree.reduce<CrmProductCategory[]>((acc, cat) => {
      acc.push(cat);
      if (cat.children) acc.push(...flattenTree(cat.children));
      return acc;
    }, []);
  }, []);

  /** Estatísticas para os KPIs */
  const stats = React.useMemo<CategoryStats>(() => {
    const allCategories = flattenTree(categoryTree);
    const parentCount = categoryTree.length;
    const childCount = allCategories.length - parentCount;

    return {
      total_count: allCategories.length,
      active_count: allCategories.filter(c => c.is_active).length,
      parent_count: parentCount,
      child_count: childCount,
    };
  }, [categoryTree, flattenTree]);

  // ============================================================================
  // FILTRO DE BUSCA
  // ============================================================================

  /** Filtra categorias na árvore recursivamente */
  const filterCategoryTree = React.useCallback((tree: CrmProductCategory[], searchTerm: string): CrmProductCategory[] => {
    if (!searchTerm.trim()) return tree;
    const searchLower = searchTerm.toLowerCase();
    
    return tree.reduce<CrmProductCategory[]>((acc, cat) => {
      const matchesSelf = cat.name.toLowerCase().includes(searchLower) ||
                          cat.description?.toLowerCase().includes(searchLower);
      const filteredChildren = cat.children ? filterCategoryTree(cat.children, searchTerm) : [];
      
      if (matchesSelf || filteredChildren.length > 0) {
        acc.push({
          ...cat,
          children: matchesSelf ? cat.children : filteredChildren,
        });
      }
      return acc;
    }, []);
  }, []);

  /** Árvore filtrada pela busca */
  const filteredTree = React.useMemo(
    () => filterCategoryTree(categoryTree, search),
    [categoryTree, search, filterCategoryTree]
  );

  // Hook de confirmação premium
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  // Estados do dialog
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingCategory, setEditingCategory] = React.useState<CrmProductCategory | null>(null);
  const [parentIdForNew, setParentIdForNew] = React.useState<string | null>(null);

  // Estados do formulário
  const [formName, setFormName] = React.useState('');
  const [formDescription, setFormDescription] = React.useState('');
  const [formParentId, setFormParentId] = React.useState<string | null>(null);
  const [formIcon, setFormIcon] = React.useState('folder');
  const [formColor, setFormColor] = React.useState('#6366f1');
  const [formWorkspaceIds, setFormWorkspaceIds] = React.useState<string[]>([]);

  /**
   * Abre dialog para nova categoria
   */
  const handleNewCategory = (parentId: string | null = null) => {
    setEditingCategory(null);
    setParentIdForNew(parentId);
    setFormName('');
    setFormDescription('');
    setFormParentId(parentId);
    setFormIcon('folder');
    setFormColor('#6366f1');
    setFormWorkspaceIds([]);
    setDialogOpen(true);
  };

  /**
   * Abre dialog para edição
   */
  const handleEdit = async (category: CrmProductCategory) => {
    setEditingCategory(category);
    setParentIdForNew(null);
    setFormName(category.name);
    setFormDescription(category.description || '');
    setFormParentId(category.parent_id);
    setFormIcon(category.icon);
    setFormColor(category.color);
    // Carregar workspaces vinculados
    try {
      const wsIds = await fetchItemWorkspaces('category', category.id);
      setFormWorkspaceIds(wsIds);
    } catch {
      setFormWorkspaceIds([]);
    }
    setDialogOpen(true);
  };

  /**
   * Salva categoria (criar ou atualizar)
   */
  const handleSave = async () => {
    if (!formName.trim()) return;
    if (formWorkspaceIds.length === 0) return;

    if (editingCategory) {
      // Atualizar
      const input: UpdateCategoryInput = {
        id: editingCategory.id,
        name: formName.trim(),
        description: formDescription.trim() || null,
        parent_id: formParentId,
        icon: formIcon,
        color: formColor,
      };
      await updateCategory.mutateAsync(input);
      // Atualizar workspaces
      await updateItemWorkspaces.mutateAsync({
        itemType: 'category',
        itemId: editingCategory.id,
        workspaceIds: formWorkspaceIds,
      });
    } else {
      // Criar
      const input: CreateCategoryInput = {
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        parent_id: formParentId,
        icon: formIcon,
        color: formColor,
      };
      const newCategory = await createCategory.mutateAsync(input);
      // Vincular workspaces
      await updateItemWorkspaces.mutateAsync({
        itemType: 'category',
        itemId: newCategory.id,
        workspaceIds: formWorkspaceIds,
      });
    }

    setDialogOpen(false);
  };

  /**
   * Handler de exclusão com validação de vínculos
   * Verifica produtos vinculados antes de permitir exclusão
   */
  const handleDeleteCategory = async (category: CrmProductCategory) => {
    // Verificar produtos vinculados à categoria
    const { count } = await supabase
      .from('crm_products')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', category.id);

    // Verificar subcategorias
    const hasChildren = category.children && category.children.length > 0;

    if ((count && count > 0) || hasChildren) {
      // Possui vínculos — informar e bloquear exclusão
      const parts: string[] = [];
      if (count && count > 0) parts.push(`${count} produto(s) vinculado(s)`);
      if (hasChildren) parts.push(`${category.children!.length} subcategoria(s)`);

      await confirmDialog({
        variant: 'info',
        title: 'Não é possível remover',
        description: `A categoria "${category.name}" possui ${parts.join(' e ')}. Remova os vínculos primeiro ou desative a categoria.`,
        confirmText: 'Entendi',
      });
    } else {
      // Sem vínculos — confirmar exclusão
      const confirmed = await confirmDialog({
        variant: 'danger',
        title: 'Remover categoria?',
        description: `Tem certeza que deseja excluir "${category.name}"? Esta ação não pode ser desfeita.`,
        confirmText: 'Remover',
      });
      if (confirmed) {
        await deleteCategory.mutateAsync(category.id);
      }
    }
  };

  /**
   * Toggle status ativo
   */
  const handleToggleActive = async (category: CrmProductCategory) => {
    await updateCategory.mutateAsync({
      id: category.id,
      is_active: !category.is_active,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* ================================================================== */}
      {/* HEADER COMPACTO - Padrão unificado CRM */}
      {/* ================================================================== */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Categorias de Produtos</h1>
        </div>
        {hasCreatePermission && (
          <Button 
            size="xs"
            onClick={() => handleNewCategory()}
            className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <Plus className="mr-1.5" />
            Nova Categoria
          </Button>
        )}
      </div>

      {/* ================================================================== */}
      {/* KPI CARDS - Padrão unificado CRM */}
      {/* ================================================================== */}
      {!isLoading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
          <MetricCard
            title="Total de Categorias"
            value={stats.total_count}
            icon={FolderTree}
            variant="primary"
          />
          <MetricCard
            title="Ativas"
            value={stats.active_count}
            icon={CheckCircle}
            variant="success"
          />
          <MetricCard
            title="Categorias Pai"
            value={stats.parent_count}
            icon={Folder}
            variant="info"
          />
          <MetricCard
            title="Subcategorias"
            value={stats.child_count}
            icon={GitBranch}
            variant="purple"
          />
        </div>
      )}

      {/* ================================================================== */}
      {/* BARRA DE FILTROS - Padrão unificado CRM */}
      {/* ================================================================== */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Busca */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar categorias..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Indicador de carregamento */}
        {isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>

      {/* ================================================================== */}
      {/* CONTEÚDO - ÁRVORE DE CATEGORIAS */}
      {/* ================================================================== */}
      <div className="flex-1 overflow-auto">
        <Card className="m-4 border-border/50 overflow-hidden bg-gradient-to-br from-background to-muted/10">
          {/* Header do card */}
          <div className="px-4 py-3 border-b border-border/50 bg-muted/20">
            <div className="flex items-center gap-3">
              <h2 className="font-medium text-sm text-foreground">Árvore de Categorias</h2>
              <Badge variant="secondary" className="font-normal text-xs">
                {filteredTree.length} raíz{filteredTree.length !== 1 ? 'es' : ''}
              </Badge>
              {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          </div>

          {/* Conteúdo */}
          <div className="p-4">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-7 w-7 rounded-md" />
                    <Skeleton className="h-5 flex-1" />
                  </div>
                ))}
              </div>
            ) : filteredTree.length === 0 ? (
              <EmptyState
                title={search ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria cadastrada'}
                description={search 
                  ? 'Tente buscar por outro termo.' 
                  : 'Comece criando a primeira categoria para organizar seus produtos.'}
                icon={FolderTree}
                action={!search ? {
                  label: 'Criar Categoria',
                  onClick: () => handleNewCategory(),
                } : undefined}
              />
            ) : (
              <div>
                {filteredTree.map(category => (
                  <CategoryNode
                    key={category.id}
                    category={category}
                    level={0}
                    onEdit={hasEditPermission ? handleEdit : undefined}
                    onAddChild={hasCreatePermission ? handleNewCategory : undefined}
                    onDelete={hasDeletePermission ? (cat) => handleDeleteCategory(cat) : undefined}
                    onToggleActive={hasEditPermission ? handleToggleActive : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Dialog de criação/edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {/* 
          CORREÇÃO DEFINITIVA DE SCROLL:
          - h-[85dvh] define altura REAL baseada na viewport dinâmica (resolve mobile)
          - max-h-[85dvh] limita crescimento
          - min-h-0 permite que o flex-child encolha e vire overflow
          - overflow-hidden impede vazamento visual
        */}
        <DialogContent className="sm:max-w-md h-[85dvh] max-h-[85dvh] min-h-0 flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle>
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
            <DialogDescription>
              {editingCategory
                ? 'Atualize as informações da categoria'
                : 'Preencha os dados para criar uma nova categoria'}
            </DialogDescription>
          </DialogHeader>

          {/* 
            ScrollArea com altura definida pelo flex:
            - flex-1: ocupa espaço restante após header/footer
            - min-h-0: permite encolher abaixo do conteúdo (CRÍTICO para overflow)
            - h-full: garante que o Viewport interno tenha referência de altura
          */}
          <ScrollArea className="flex-1 min-h-0 h-full pr-4">
            <div className="space-y-4 py-4">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ex: Eletrônicos"
                />
              </div>

              {/* Descrição */}
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Descrição opcional da categoria"
                  rows={2}
                />
              </div>

              {/* Categoria pai */}
              <div className="space-y-2">
                <Label>Categoria Pai</Label>
                <CategoryTreeSelect
                  value={formParentId}
                  onChange={setFormParentId}
                  excludeId={editingCategory?.id}
                  placeholder="Selecione (opcional)"
                />
              </div>

              {/* Workspaces */}
              <div className="space-y-2">
                <Label>Workspaces *</Label>
                <WorkspaceMultiSelect
                  value={formWorkspaceIds}
                  onChange={setFormWorkspaceIds}
                  placeholder="Selecione pelo menos um workspace..."
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Selecione os workspaces onde esta categoria estará disponível
                </p>
              </div>

              {/* Ícone */}
              <div className="space-y-2">
                <Label>Ícone</Label>
                <div className="flex flex-wrap gap-1">
                  {AVAILABLE_ICONS.map(({ name, icon: Icon }) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setFormIcon(name)}
                      className={cn(
                        'h-9 w-9 rounded-md border flex items-center justify-center transition-colors',
                        formIcon === name
                          ? 'border-primary bg-primary/10'
                          : 'border-input hover:bg-muted'
                      )}
                    >
                      <Icon className="h-4 w-4" style={{ color: formColor }} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Cor */}
              <div className="space-y-2">
                <Label>Cor</Label>
                <div className="flex flex-wrap gap-1">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormColor(color)}
                      className={cn(
                        'h-7 w-7 rounded-full border-2 transition-transform',
                        formColor === color
                          ? 'border-foreground scale-110'
                          : 'border-transparent hover:scale-105'
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formName.trim() || formWorkspaceIds.length === 0 || createCategory.isPending || updateCategory.isPending}
            >
              {editingCategory ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ConfirmDialog premium (renderizado pelo hook useConfirmDialog) */}
      {ConfirmDialogComponent}
    </div>
  );
}
